import {
  createContext,
  useContext,
  useEffect,
  PropsWithChildren,
  JSX,
} from "react";
import { Navigate } from "react-router-dom";
import { AuthError, createClient, Session } from "@supabase/supabase-js";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { useLocalStorage } from "../hooks/useLocalStorage";
import client from "../api/ApiClient";
import { authRequestPath } from "./ProtectedRoute";

export const tokenCookieKey = "sb-lpydbkeaswhgwpfxurpw-auth-token";
export const authorizerDenyKey = "refresh-authorizer-deny";

interface AuthProviderType {
  session: Session | null;
  login: () => JSX.Element;
  logout: () => Promise<{
    error: AuthError | null;
  }>;
}

const AuthContext = createContext<AuthProviderType>({
  session: null,
  login: () => <></>,
  logout: () =>
    Promise.resolve({
      error: null,
    }),
});
// TODO: Inject as local vars
const projUrl = "https://lpydbkeaswhgwpfxurpw.supabase.co";
const publicKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxweWRia2Vhc3doZ3dwZnh1cnB3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3MjU5MDM4MDAsImV4cCI6MjA0MTQ3OTgwMH0.20QHDJzuA_SwEPbvNVRhrP8AiWyXF41s1jjQnj0PPVQ";
const supabase = createClient(projUrl, publicKey);

export default function AuthProvider({ children }: PropsWithChildren) {
  const myOrigin = window.location.origin;
  // We were previously using state but session should be stored as var for reusability
  // example: useState<Session | null>(null);
  const { storedValue: session, setValue: setSession } =
    useLocalStorage<Session | null>(tokenCookieKey, null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, _session) => {
      if (_event === "PASSWORD_RECOVERY") {
        // TODO: Add a custom password recovery page
        const newPassword = prompt("Input your new password");
        supabase.auth
          .updateUser({ password: newPassword ?? undefined })
          .then(({ data, error }) => {
            if (data) alert("Password updated successfully!");
            if (error) alert("There was an error updating your password.");
          });
      } else {
        setSession(_session);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = () => {
    if (!session) {
      // TODO: Add social login providers
      // TODO: Enable links for forgot password and signup
      // TODO: Need to set up SMTP service for emailing
      // TODO: Look at adding a custom signup page
      // Supabase Signup Ref: https://supabase.com/docs/guides/auth/managing-user-data?queryGroups=language&language=js#adding-and-retrieving-user-metadata
      return (
        <Auth
          supabaseClient={supabase}
          appearance={{ theme: ThemeSupa }}
          localization={{
            variables: {
              sign_in: {
                email_label: "",
                password_label: "",
                button_label: "Log In",
              },
              sign_up: {
                email_label: "",
                password_label: "",
                button_label: "Sign up for free",
                link_text: "Don't have an account? Sign up for free",
              },
            },
          }}
          providers={["google"]}
          showLinks={true}
          redirectTo={`${myOrigin}/login`}
        />
      );
    }

    const getAuthRequestPath =
      window.localStorage.getItem(authRequestPath) ?? "/";
    // Clear any stored auth request path
    window.localStorage.removeItem(authRequestPath);
    return <Navigate to={getAuthRequestPath} replace />;
  };

  const logout = async () => {
    if (session) {
      client.cache.reset();
      return supabase.auth.signOut();
    }
    return {
      error: null,
    };
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
